import { useEffect, useState } from 'react';

// Sin `timeout` global en axios, una petición colgada dejaría al consumidor esperando para siempre.
const RESOURCE_TIMEOUT_MS = 5000;

// `isCacheable` lo decide cada recurso: solo las respuestas deterministas se guardan; el resto se reintenta.
export type ResourceLoad<T> = {
  value: T;
  isCacheable: boolean;
};

export type CachedResourceResult<T> = {
  value: T;
  isReady: boolean;
};

type ResourceState<T> = CachedResourceResult<T> & {
  key: string | undefined;
};

export type CachedProgramResource<T> = {
  useResource: (programGuid: string | undefined) => CachedResourceResult<T>;
  clearCache: () => void;
};

// Los valores son JSON pequeños: la comparación estructural evita renders cuando la revalidación trae lo mismo.
function isSameValue<T>(current: T, next: T): boolean {
  return current === next || JSON.stringify(current) === JSON.stringify(next);
}

function isSameState<T>(current: ResourceState<T>, next: ResourceState<T>): boolean {
  return current.key === next.key && current.isReady === next.isReady && isSameValue(current.value, next.value);
}

function keepIfSame<T>(next: ResourceState<T>): (previous: ResourceState<T>) => ResourceState<T> {
  return (previous) => (isSameState(previous, next) ? previous : next);
}

export function createCachedProgramResource<T>(loadResource: (programGuid: string) => Promise<ResourceLoad<T>>, emptyValue: T): CachedProgramResource<T> {
  // Se sirve lo cacheado para que el visor no espere y se revalida para que los cambios hechos en BO se vean al reabrir.
  const cache = new Map<string, { value: T }>();
  // Promesas en vuelo por clave, para deduplicar montajes concurrentes del mismo programa.
  const inFlightByKey = new Map<string, Promise<T>>();

  // El valor va envuelto para que un resultado vacío cacheado se distinga de "aún no pedido".
  function readState(programGuid: string | undefined): ResourceState<T> {
    if (!programGuid) return { key: programGuid, value: emptyValue, isReady: true };

    const cached = cache.get(programGuid);
    if (cached) return { key: programGuid, value: cached.value, isReady: true };

    return { key: programGuid, value: emptyValue, isReady: false };
  }

  function withTimeout(loading: Promise<ResourceLoad<T>>): Promise<ResourceLoad<T>> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => resolve({ value: emptyValue, isCacheable: false }), RESOURCE_TIMEOUT_MS);

      loading.then(
        (loaded) => {
          clearTimeout(timeoutId);
          resolve(loaded);
        },
        (error) => {
          clearTimeout(timeoutId);
          reject(error);
        }
      );
    });
  }

  // `fetchResource` resuelve después de cachear, así que un fallo o un 500 conserva el último valor bueno en vez de vaciarlo.
  function readLoadedState(programGuid: string, loadedValue: T): ResourceState<T> {
    const cached = cache.get(programGuid);

    return { key: programGuid, value: cached ? cached.value : loadedValue, isReady: true };
  }

  async function fetchResource(programGuid: string): Promise<T> {
    const existing = inFlightByKey.get(programGuid);
    // Comparación explícita (no truthy-check de la Promise) para no disparar S6544 de Sonar.
    if (existing !== undefined) return existing;

    // El wrapper convierte un throw síncrono del loader en rechazo: si no, el `finally` borraría la clave antes de registrarla.
    const loading = Promise.resolve().then(() => loadResource(programGuid));

    // Se cachea aquí y no tras el `await`: una respuesta que llega después del timeout sigue sirviendo al siguiente montaje.
    loading
      .then((loaded) => {
        if (loaded.isCacheable) cache.set(programGuid, { value: loaded.value });
      })
      .catch(() => undefined);

    const promise = (async (): Promise<T> => {
      try {
        const loaded = await withTimeout(loading);
        return loaded.value;
      } catch {
        // Excepción real (red caída, JSON roto): fail-open sin cachear.
        return emptyValue;
      } finally {
        inFlightByKey.delete(programGuid);
      }
    })();

    inFlightByKey.set(programGuid, promise);
    return promise;
  }

  function useResource(programGuid: string | undefined): CachedResourceResult<T> {
    const [state, setState] = useState<ResourceState<T>>(() => readState(programGuid));

    useEffect(() => {
      setState(keepIfSame(readState(programGuid)));

      if (!programGuid) return undefined;

      let isCancelled = false;
      fetchResource(programGuid).then((value) => {
        if (isCancelled) return;
        setState(keepIfSame(readLoadedState(programGuid, value)));
      });

      return () => {
        isCancelled = true;
      };
    }, [programGuid]);

    // El estado se reconcilia en el efecto, que corre tras el commit: sin esto un cambio de programa
    // publicaría un render con los datos del anterior.
    const { value, isReady } = state.key === programGuid ? state : readState(programGuid);

    return { value, isReady };
  }

  function clearCache(): void {
    cache.clear();
    inFlightByKey.clear();
  }

  return { useResource, clearCache };
}
